import { useId, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { AppShell, Button, Input } from '@frm/ui';
import {
  loginWithBrs,
  tenantDomainToLabel,
  tenantLabelToDomain,
  validateBrsCredentials,
  validateTenantLabel,
} from '@/lib/auth';
import { assignLocation } from '@/lib/browser';

const LAST_TENANT_STORAGE_KEY = 'frm.launcher.lastTenant';

function readLastTenant(): string {
  try {
    return tenantDomainToLabel(window.localStorage.getItem(LAST_TENANT_STORAGE_KEY) ?? '');
  } catch {
    return '';
  }
}

function saveLastTenant(tenant: string): void {
  try {
    window.localStorage.setItem(LAST_TENANT_STORAGE_KEY, tenant);
  } catch {
    // Storage may be disabled; login must still work.
  }
}

export default function LoginPage() {
  const formId = useId();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantLabel, setTenantLabel] = useState(readLastTenant);
  const [remember, setRemember] = useState(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const errorId = `${formId}-error`;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (pending) return;

    const tenantValidationError = validateTenantLabel(tenantLabel);
    if (tenantValidationError) {
      setTenantError(tenantValidationError);
      setError(null);
      return;
    }

    const credentialsValidationError = validateBrsCredentials(email, password);
    if (credentialsValidationError) {
      setError(credentialsValidationError);
      return;
    }

    const normalizedTenant = tenantLabelToDomain(tenantLabel);
    saveLastTenant(normalizedTenant);

    setPending(true);
    setError(null);
    setTenantError(null);

    const nextRaw = searchParams.get('next');
    const result = await loginWithBrs({
      email,
      password,
      remember,
      tenant: normalizedTenant,
      next: nextRaw ?? undefined,
    });
    if (result.ok) {
      setPending(false);
      // Бэк уже валидировал `redirect` через safeReturnUrl(). Абсолютный URL
      // (cross-subdomain SSO) ⇒ full navigation; относительный ⇒ SPA-роутер.
      const target = result.redirect;
      if (/^https?:\/\//i.test(target) || target.startsWith('/api/auth/handoff?')) {
        assignLocation(target);
      } else {
        navigate(safeReturnPath(target), { replace: true });
      }
    } else {
      if (result.message === 'Укажите разрешённый домен организации.') {
        setTenantError(result.message);
      } else {
        setError(result.message);
      }
      setPending(false);
      return;
    }
  }

  return (
    <AppShell mode="workbench" contentPreset="standard">
      <div className="login-page" aria-labelledby="login-title">
        <section className="login-panel">
        <div className="login-panel__brand">
          <span className="login-panel__mark" aria-hidden="true">ф</span>
          <div>
            <div className="login-panel__product">ФРМ.AI</div>
            <div className="login-panel__scope">Единый вход в экосистему</div>
          </div>
        </div>

        <form className="login-panel__content" onSubmit={onSubmit} noValidate aria-busy={pending}>
          <p className="login-panel__eyebrow">BRS Basis Security</p>
          <h1 id="login-title" className="login-panel__title">
            Вход в ФРМ.AI
          </h1>
          <p className="login-panel__text">
            Введите email и пароль от BRS. После входа откроется каталог
            доступных AI-сервисов и последних рабочих инструментов.
          </p>

          <label className="auth-field" htmlFor={`${formId}-email`}>
            <span>Email</span>
            <input
              id={`${formId}-email`}
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (error) setError(null);
              }}
              disabled={pending}
              aria-invalid={!!error}
              aria-describedby={error ? errorId : undefined}
            />
          </label>

          <Input
            id={`${formId}-tenant`}
            label="Домен организации"
            aria-label="Домен организации"
            type="text"
            autoComplete="organization"
            placeholder="zaoblako"
            required
            value={tenantLabel}
            onChange={(event) => {
              setTenantLabel(event.target.value);
              if (tenantError) setTenantError(null);
              if (error) setError(null);
            }}
            disabled={pending}
            error={tenantError || undefined}
            rightSlot={(
              <span className="tenant-field__suffix" aria-hidden="true">
                .zafrm.ru
              </span>
            )}
          />

          <label className="auth-field" htmlFor={`${formId}-password`}>
            <span>Пароль</span>
            <input
              id={`${formId}-password`}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                if (error) setError(null);
              }}
              disabled={pending}
              aria-invalid={!!error}
              aria-describedby={error ? errorId : undefined}
            />
          </label>

          <label className="auth-check">
            <input
              type="checkbox"
              checked={remember}
              onChange={(event) => setRemember(event.target.checked)}
              disabled={pending}
            />
            <span>Запомнить на 7 дней</span>
          </label>

          {error && (
            <p id={errorId} className="auth-error" role="alert">
              {error}
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            loading={pending}
            disabled={!email.trim() || !password || !tenantLabel.trim()}
            fullWidth
            className="login-panel__cta"
          >
            {pending ? 'Входим' : 'Войти'}
          </Button>
        </form>

        <div className="login-panel__meta" aria-label="Доступные разделы">
          <span>Метрики</span>
          <span>Маркет</span>
          <span>Рента</span>
        </div>
        </section>

        <aside className="login-preview" aria-hidden="true">
        <div className="login-preview__topline" />
        <div className="login-preview__grid">
          <span />
          <span />
          <span />
          <span />
        </div>
        <div className="login-preview__rows">
          <span />
          <span />
          <span />
        </div>
        </aside>
      </div>
    </AppShell>
  );
}

// Намеренный второй слой, а не дубль серверного safeReturnUrl (L-4 в
// AUDIT_2026-06-02). Сюда попадает только относительный target (абсолютные
// идут в assignLocation выше), и эта проверка защищает sink react-router
// `navigate()` от protocol-relative `//evil.com`, который не матчит
// `^https?://` и иначе ушёл бы в SPA-навигацию. Сервер тоже режет `//`,
// так что это defense-in-depth на security-чувствительном редиректе.
function safeReturnPath(value: string | null): string {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return '/';
  return value;
}
